import {
  Button,
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue } from 'jotai';
import CategoryTreeInput from 'lib-knowledge-management-common/components/categoryTreeInput/index';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import { CategoryRef } from 'lib-knowledge-management-common/models/types';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { collectionIdAtom } from 'models/collectionStore';
import { useContext, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import useArticleCreate from 'hooks/useArticleCreate';

import {
  ArticleAddModalSchema,
  articleAddModalSchema,
} from './articleAddModalSchema';
import { CollectionSelect } from './collectionSelect';
import { TitleInput } from './titleInput';

type ArticleAddProps = {
  presetParent?: CategoryRef;
};

export function ArticleAddModal({
  presetParent,
}: ArticleAddProps): JSX.Element {
  const { hideModal } = useModal();
  const navigate = useNavigate();
  const { errorNotify } = useNotification();
  const { selectedLanguage, enabledLanguages } = useContext(LanguageContext);
  const initialCollectionId = useAtomValue(collectionIdAtom);

  const {
    control,
    setValue,
    resetField,
    watch,
    formState: { isValid: isFormValid },
    handleSubmit,
  } = useForm<ArticleAddModalSchema>({
    resolver: zodResolver(articleAddModalSchema),
    defaultValues: {
      articleTitle: '',
      // Undefined so we show the placeholder
      collectionId: initialCollectionId
        ? [{ id: initialCollectionId }]
        : undefined,

      language: {
        id: selectedLanguage.languageId,
        name: selectedLanguage.description,
      },
      parentRef: presetParent,
    },
    reValidateMode: 'onChange',
    mode: 'all',
  });

  const artTitle = watch('articleTitle');
  const parentObj = watch('parentRef');
  const collectionId = watch('collectionId');

  // Reset the parentRef field if the collection changes
  const collectionIdRef = useRef(collectionId);
  useEffect(() => {
    if (collectionIdRef.current?.[0].id !== collectionId?.[0].id) {
      resetField('parentRef');
      // @ts-expect-error - clear field so you can't select a category that belongs to another collection
      setValue('parentRef', null);
      collectionIdRef.current = collectionId;
    }
  }, [collectionId, resetField, setValue]);

  const [createArticle, { loading }] = useArticleCreate({
    onCompleted: (res) => {
      if (!res.articleCreate) {
        errorNotify({
          text: `"${artTitle}" could not be created`,
        });
        return;
      }

      hideModal();
      const newArticleRoute = routeGenerator.articleEdit({
        articleId: res.articleCreate.id,
        collectionId: initialCollectionId,
      });
      navigate(newArticleRoute);
    },
    onError: () => {
      errorNotify({
        text: `"${artTitle}" could not be created`,
      });
    },
  });

  return (
    <ModalWrapper>
      <form
        onSubmit={handleSubmit(({ articleTitle, language, parentRef }) => {
          createArticle({
            title: articleTitle,
            languageId: language.id,
            parentObj: parentRef,
          });
        })}
      >
        <ModalHead title="Create Article" onClose={hideModal}></ModalHead>
        <ModalBody>
          <TitleInput
            watch={watch}
            control={control}
            required
            enabledLanguages={enabledLanguages}
          />

          <CollectionSelect
            control={control}
            setValue={setValue}
            initialId={initialCollectionId}
          />

          <Controller
            control={control}
            name="parentRef"
            render={({ field }) => (
              <CategoryTreeInput
                withCollectionOption={false}
                value={parentObj}
                required
                onChange={(val) => field.onChange(val)}
                currentCategoryId={undefined}
                collectionId={collectionId?.[0].id}
              />
            )}
          />
        </ModalBody>

        <ModalFoot>
          <FlexBox justifyContent="flex-end" gap="100">
            <Button disabled={loading} variant="tertiary" onClick={hideModal}>
              Cancel
            </Button>

            <Button type="submit" disabled={loading || !isFormValid}>
              Save
            </Button>
          </FlexBox>
        </ModalFoot>
      </form>
    </ModalWrapper>
  );
}
